import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { listCustomers } from '../../services/customerIdentityApi';

const DEBOUNCE_MS = 300;
const RESULT_LIMIT = 10;

/**
 * Type-ahead customer filter -- replaces a plain <select> populated with
 * every customer on the account (an account can have thousands, e.g. 2000+
 * on one real "shop" account; loading all of them to populate a dropdown
 * doesn't scale and was slow/wasteful for accounts that size).
 *
 * Instead this searches server-side as the admin types (debounced), via the
 * same `listCustomers({search, ...})` call already used elsewhere -- it
 * matches customer name, email, and ERP id (see
 * CustomerService._customers_filter_sql). Once a customer is picked, the
 * input shows their name as a read-only chip with a clear ("x") button;
 * clearing resets back to "all customers" and re-enables typing.
 *
 * The results list is rendered through a portal into document.body,
 * position: fixed at the input's current screen coordinates -- `.ci-card`
 * (the filters card this sits inside) sets `overflow: hidden` to clip its
 * children to its rounded corners, which was clipping the dropdown instead
 * of just letting it float above the card.
 */
export default function CustomerSearchPicker({
  siteId,
  value,
  valueLabel,
  onSelect,
  onClear,
  disabled,
  id,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [menuRect, setMenuRect] = useState(null);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);

  const runSearch = useCallback(async (text) => {
    const trimmed = text.trim();
    if (!trimmed) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const params = { search: trimmed, limit: RESULT_LIMIT };
      if (siteId) params.site_id = siteId;
      const rows = await listCustomers(params);
      setResults(Array.isArray(rows) ? rows : []);
    } catch (_err) {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [siteId]);

  const updateMenuRect = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    setMenuRect({ top: rect.bottom, left: rect.left, width: rect.width });
  }, []);

  const handleQueryChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    updateMenuRect();
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(text), DEBOUNCE_MS);
  };

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  // Keep the floating menu aligned with the input while it's open (window
  // resize, or the page scrolling underneath a fixed-position menu).
  useEffect(() => {
    if (!open) return undefined;
    updateMenuRect();
    window.addEventListener('resize', updateMenuRect);
    window.addEventListener('scroll', updateMenuRect, true);
    return () => {
      window.removeEventListener('resize', updateMenuRect);
      window.removeEventListener('scroll', updateMenuRect, true);
    };
  }, [open, updateMenuRect]);

  // Close the results dropdown on an outside click -- the menu itself now
  // lives in a portal, so "outside" means outside BOTH the input and the
  // portaled menu element.
  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      const inInput = inputRef.current && inputRef.current.contains(e.target);
      const inMenu = menuRef.current && menuRef.current.contains(e.target);
      if (!inInput && !inMenu) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handlePick = (customer) => {
    setOpen(false);
    setQuery('');
    setResults([]);
    onSelect(customer.id, customer.name || customer.id);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setOpen(false);
    onClear();
  };

  if (value) {
    return (
      <div className="d-flex align-items-center gap-2">
        <span className="form-control d-flex align-items-center justify-content-between">
          <span className="text-truncate">{valueLabel || value}</span>
        </span>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm flex-shrink-0"
          onClick={handleClear}
          disabled={disabled}
          aria-label="Clear customer filter"
        >
          <i className="bi bi-x-lg" />
        </button>
      </div>
    );
  }

  const showMenu = open && (query.trim() || searching) && menuRect;

  return (
    <div>
      <input
        ref={inputRef}
        id={id}
        type="text"
        className="form-control"
        placeholder="Type a customer name, email, or ERP id…"
        value={query}
        onChange={handleQueryChange}
        onFocus={() => { updateMenuRect(); if (query.trim()) setOpen(true); }}
        disabled={disabled}
        autoComplete="off"
      />
      {showMenu
        ? createPortal(
          <div
            ref={menuRef}
            className="list-group shadow-sm"
            style={{
              position: 'fixed',
              top: menuRect.top,
              left: menuRect.left,
              width: menuRect.width,
              zIndex: 2000,
              maxHeight: 280,
              overflowY: 'auto',
            }}
          >
            {searching ? (
              <div className="list-group-item small text-muted">Searching…</div>
            ) : results.length === 0 ? (
              <div className="list-group-item small text-muted">No matching customers.</div>
            ) : (
              results.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  className="list-group-item list-group-item-action"
                  onClick={() => handlePick(c)}
                >
                  <div className="fw-semibold">{c.name || c.id}</div>
                  <div className="small text-muted">
                    {[c.email, c.erp_customer_id].filter(Boolean).join(' · ') || '—'}
                  </div>
                </button>
              ))
            )}
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}
